// src/components/AddSubscriber.jsx
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import axios from 'axios';

const AddSubscriber = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [serverError, setServerError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [subscribers, setSubscribers] = useState([]);
  const [loadingSubscribers, setLoadingSubscribers] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(null);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
    reset
  } = useForm();

  const password = watch('password');

  // Fetch subscribers on component mount
  React.useEffect(() => {
    fetchSubscribers();
  }, []);

  const fetchSubscribers = async () => {
    setLoadingSubscribers(true);
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/subscribers`);
      setSubscribers(Array.isArray(response.data) ? response.data : response.data.data || []);
    } catch (error) {
      console.error('Error fetching subscribers:', error);
    } finally {
      setLoadingSubscribers(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this subscriber?')) {
      return;
    }

    setDeleteLoading(id);
    try {
      await axios.delete(`${process.env.REACT_APP_API_BASE_URL}/api/subscribers/${id}`);
      setSubscribers(subscribers.filter(sub => sub.id !== id));
    } catch (error) {
      alert('Error deleting subscriber: ' + (error.response?.data?.message || 'Please try again'));
    } finally {
      setDeleteLoading(null);
    }
  };

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    setServerError('');
    setSuccessMessage('');

    try {
      const response = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/subscribers`, data);
      
      if (response.data.message) {
        setSuccessMessage(response.data.message);
        reset();
        fetchSubscribers();
        setTimeout(() => setSuccessMessage(''), 3000);
      }
    } catch (error) {
      if (error.response && error.response.data) {
        const errors = error.response.data.errors;
        let errorMessage = '';

        if (errors) {
          errorMessage = Object.values(errors).flat().join(', ');
        } else if (error.response.data.message) {
          errorMessage = error.response.data.message;
        } else {
          errorMessage = 'An error occurred. Please try again.';
        }

        // Check if error is related to mobile uniqueness constraint
        if (errorMessage.toLowerCase().includes('mobile') && 
            (errorMessage.includes('Duplicate') || errorMessage.includes('unique') || errorMessage.includes('already'))) {
          setServerError('Mobile already taken.');
        } else {
          setServerError(errorMessage);
        }
      } else {
        setServerError('Network error. Please check your connection.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-base-200 via-base-100 to-base-200 py-12 px-4 sm:px-6 lg:px-8 flex items-center justify-center">
      <div className="w-full max-w-3xl">
        <div className="card bg-base-100 shadow-2xl border border-base-300">
          <div className="card-body p-8 sm:p-10">
            {/* Header Section */}
            <div className="text-center mb-8">
              <div className="badge badge-lg badge-primary mb-4 text-white font-semibold">
                New Registration
              </div>
              <h2 className="card-title text-3xl font-bold bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
                Library Subscriber Registration
              </h2>
              <p className="text-base-content/70 mt-2 text-sm">
                Create your account to access our library services
              </p>
            </div>

            <div className="divider"></div>
            
            {/* Success Message */}
            {successMessage && (
              <div className="alert alert-success shadow-lg mb-6 animate-pulse">
                <svg xmlns="http://www.w3.org/2000/svg" className="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="font-semibold">{successMessage}</span>
              </div>
            )}
            
            {/* Error Message */}
            {serverError && (
              <div className="alert alert-error shadow-lg mb-6">
                <svg xmlns="http://www.w3.org/2000/svg" className="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4v2m0-12a9 9 0 110 18 9 9 0 010-18z" />
                </svg>
                <span className="font-semibold">{serverError}</span>
              </div>
            )}

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              {/* Personal Information Section */}
              <div>
                <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
                  <span className="badge badge-primary text-white">1</span>
                  Personal Information
                </h3>
                <div className="space-y-5">
                  {/* Name Field */}
                  <div className="form-control">
                    <label className="label pb-2">
                      <span className="label-text font-semibold text-base">Full Name</span>
                      <span className="badge badge-sm badge-error text-white">Required</span>
                    </label>
                    <input
                      type="text"
                      placeholder="John Doe"
                      className={`input input-bordered input-lg focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all ${errors.name ? 'input-error' : ''}`}
                      {...register('name', { required: 'Name is required' })}
                    />
                    {errors.name && (
                      <label className="label pt-2">
                        <span className="label-text-alt text-error font-semibold">{errors.name.message}</span>
                      </label>
                    )}
                  </div>

                  {/* Email Field */}
                  <div className="form-control">
                    <label className="label pb-2">
                      <span className="label-text font-semibold text-base">Email Address</span>
                      <span className="badge badge-sm badge-error text-white">Required</span>
                    </label>
                    <input
                      type="email"
                      placeholder="john@example.com"
                      className={`input input-bordered input-lg focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all ${errors.email ? 'input-error' : ''}`}
                      {...register('email', {
                        required: 'Email is required',
                        pattern: {
                          value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                          message: 'Invalid email address'
                        }
                      })}
                    />
                    {errors.email && (
                      <label className="label pt-2">
                        <span className="label-text-alt text-error font-semibold">{errors.email.message}</span>
                      </label>
                    )}
                  </div>

                  {/* Mobile Field */}
                  <div className="form-control">
                    <label className="label pb-2">
                      <span className="label-text font-semibold text-base">Mobile Number</span>
                      <span className="badge badge-sm badge-error text-white">Required</span>
                    </label>
                    <input
                      type="tel"
                      placeholder="+1 (555) 000-0000"
                      className={`input input-bordered input-lg focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all ${errors.mobile ? 'input-error' : ''}`}
                      {...register('mobile', {
                        required: 'Mobile number is required',
                        pattern: {
                          value: /^[0-9]{10,15}$/,
                          message: 'Invalid mobile number (10-15 digits)'
                        }
                      })}
                    />
                    {errors.mobile && (
                      <label className="label pt-2">
                        <span className="label-text-alt text-error font-semibold">{errors.mobile.message}</span>
                      </label>
                    )}
                  </div>

                  {/* Type Field - Dropdown */}
                  <div className="form-control">
                    <label className="label pb-2">
                      <span className="label-text font-semibold text-base">Type</span>
                      <span className="badge badge-sm badge-error text-white">Required</span>
                    </label>
                    <select
                      className={`select select-bordered select-lg focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all ${errors.type ? 'select-error' : ''}`}
                      {...register('type', { required: 'Type is required' })}
                    >
                      <option value="">Select type</option>
                      <option value="Subscriber">Subscriber</option>
                      <option value="User">User</option>
                    </select>
                    {errors.type && (
                      <label className="label pt-2">
                        <span className="label-text-alt text-error font-semibold">{errors.type.message}</span>
                      </label>
                    )}
                  </div>

                  {/* Address Field */}
                  <div className="form-control">
                    <label className="label pb-2">
                      <span className="label-text font-semibold text-base">Address</span>
                      <span className="badge badge-sm badge-error text-white">Required</span>
                    </label>
                    <textarea
                      placeholder="Enter your full address"
                      className={`textarea textarea-bordered textarea-lg focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all ${errors.address ? 'textarea-error' : ''}`}
                      rows="3"
                      {...register('address', { required: 'Address is required' })}
                    ></textarea>
                    {errors.address && (
                      <label className="label pt-2">
                        <span className="label-text-alt text-error font-semibold">{errors.address.message}</span>
                      </label>
                    )}
                  </div>
                </div>
              </div>

              <div className="divider"></div>

              {/* Security Section */}
              <div>
                <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
                  <span className="badge badge-primary text-white">2</span>
                  Security Settings
                </h3>
                <div className="space-y-5">
                  {/* Password Field */}
                  <div className="form-control">
                    <label className="label pb-2">
                      <span className="label-text font-semibold text-base">Password</span>
                      <span className="badge badge-sm badge-error text-white">Required</span>
                    </label>
                    <input
                      type="password"
                      placeholder="••••••••"
                      className={`input input-bordered input-lg focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all ${errors.password ? 'input-error' : ''}`}
                      {...register('password', {
                        required: 'Password is required',
                        minLength: {
                          value: 8,
                          message: 'Password must be at least 8 characters'
                        }
                      })}
                    />
                    {errors.password && (
                      <label className="label pt-2">
                        <span className="label-text-alt text-error font-semibold">{errors.password.message}</span>
                      </label>
                    )}
                    <label className="label pt-2">
                      <span className="label-text-alt text-sm">Minimum 8 characters recommended</span>
                    </label>
                  </div>

                  {/* Confirm Password Field */}
                  <div className="form-control">
                    <label className="label pb-2">
                      <span className="label-text font-semibold text-base">Confirm Password</span>
                      <span className="badge badge-sm badge-error text-white">Required</span>
                    </label>
                    <input
                      type="password"
                      placeholder="••••••••"
                      className={`input input-bordered input-lg focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all ${errors.password_confirmation ? 'input-error' : ''}`}
                      {...register('password_confirmation', {
                        required: 'Please confirm your password',
                        validate: value => value === password || 'Passwords do not match'
                      })}
                    />
                    {errors.password_confirmation && (
                      <label className="label pt-2">
                        <span className="label-text-alt text-error font-semibold">{errors.password_confirmation.message}</span>
                      </label>
                    )}
                  </div>
                </div>
              </div>

              <div className="divider"></div>

              {/* Submit Button */}
              <div className="form-control pt-4">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`btn btn-primary btn-lg font-bold text-white transition-all ${
                    isSubmitting
                      ? 'btn-disabled opacity-75 cursor-not-allowed' 
                      : 'hover:shadow-lg active:scale-95'
                  }`}
                >
                  {isSubmitting ? (
                    <>
                      <span className="loading loading-spinner loading-sm"></span>
                      Registering...
                    </>
                  ) : (
                    'Register Subscriber'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Subscribers List */}
        <div className="card bg-base-100 shadow-2xl border border-base-300 mt-8">
          <div className="card-body">
            <h2 className="card-title text-2xl font-bold mb-6">
              Registered Subscribers ({subscribers.length})
            </h2>

            {loadingSubscribers ? (
              <div className="flex justify-center py-8">
                <span className="loading loading-spinner loading-lg text-primary"></span>
              </div>
            ) : subscribers.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-base-content/70">No subscribers yet</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="table table-zebra w-full">
                  <thead>
                    <tr className="bg-base-200">
                        <th>ID</th>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Mobile</th>
                      <th>Password</th>
                      <th>Type</th>
                      <th>Address</th>
                      <th>Status</th>
                      <th className="text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {subscribers.map((subscriber) => (
                      <tr key={subscriber.id}>
                        <td>{subscriber.id}</td>
                        <td className="font-medium">{subscriber.name}</td>
                        <td>{subscriber.email}</td>
                        <td>{subscriber.mobile}</td>
                        <td>{subscriber.password}</td>
                        <td>{subscriber.type}</td>
                        <td>{subscriber.address}</td>
                        <td>{subscriber.status}</td>
                        <td className="text-right">
                          <button
                            onClick={() => handleDelete(subscriber.id)}
                            disabled={deleteLoading === subscriber.id}
                            className={`btn btn-sm btn-error text-white ${
                              deleteLoading === subscriber.id ? 'loading' : ''
                            }`}
                          >
                            {deleteLoading === subscriber.id ? '' : 'Delete'}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddSubscriber;